import { useEffect, useMemo, useState } from "react";

import SearchIcon from "@mui/icons-material/Search";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";

import type { MessagingPatient, MessagingTutorWithPets } from "../types";

type Props = {
  tutorsGrouped: MessagingTutorWithPets[];
  tutorsForSelection: MessagingPatient[];
  isLoading: boolean;
  hasError: boolean;
  selectedTutors: MessagingPatient[];
  onSelectedTutorsChange: (tutors: MessagingPatient[]) => void;
};

function isTutorSelected(
  selectedTutors: MessagingPatient[],
  tutor: MessagingPatient,
): boolean {
  return selectedTutors.some((t) => t.id === tutor.id);
}

function buildTutorLookupIndex(
  tutorsGrouped: MessagingTutorWithPets[],
): Record<string, MessagingTutorWithPets> {
  const index: Record<string, MessagingTutorWithPets> = {};
  for (const group of tutorsGrouped) {
    index[group.tutor.id] = group;
  }
  return index;
}

function normalizeSearch(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/gi, "");
}

export function TutorSelectionList({
  tutorsGrouped,
  tutorsForSelection,
  isLoading,
  hasError,
  selectedTutors,
  onSelectedTutorsChange,
}: Props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const groupedIndex = useMemo(
    () => buildTutorLookupIndex(tutorsGrouped),
    [tutorsGrouped],
  );

  const areAllSelected = useMemo(() => {
    if (tutorsForSelection.length === 0) return false;
    return tutorsForSelection.every((tutor) =>
      isTutorSelected(selectedTutors, tutor),
    );
  }, [selectedTutors, tutorsForSelection]);

  const filteredTutors = useMemo(() => {
    const term = String(searchTerm ?? "").trim();
    if (!term) return tutorsForSelection;

    const termLower = term.toLowerCase();
    const termNormalized = normalizeSearch(term);

    return tutorsForSelection.filter((tutor) => {
      const nameMatch = String(tutor.fullName ?? "")
        .toLowerCase()
        .includes(termLower);

      const cpfMatch = normalizeSearch(String(tutor.cpf ?? "")).includes(
        termNormalized,
      );

      const group = groupedIndex[tutor.id];
      const petMatch =
        group?.pets?.some((pet) => {
          const petNameMatch = String(pet.fullName ?? "")
            .toLowerCase()
            .includes(termLower);
          const petCpfMatch = normalizeSearch(String(pet.cpf ?? "")).includes(
            termNormalized,
          );
          return petNameMatch || petCpfMatch;
        }) ?? false;

      return nameMatch || cpfMatch || petMatch;
    });
  }, [groupedIndex, searchTerm, tutorsForSelection]);

  const paginatedTutors = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return filteredTutors.slice(startIndex, endIndex);
  }, [filteredTutors, page, rowsPerPage]);

  const handleToggleTutor = (tutor: MessagingPatient) => {
    const alreadySelected = isTutorSelected(selectedTutors, tutor);
    if (alreadySelected) {
      onSelectedTutorsChange(selectedTutors.filter((t) => t.id !== tutor.id));
      return;
    }
    onSelectedTutorsChange([...selectedTutors, tutor]);
  };

  const handleSelectAllClick = () => {
    if (areAllSelected) {
      onSelectedTutorsChange([]);
      return;
    }
    onSelectedTutorsChange(tutorsForSelection);
  };

  return (
    <Stack spacing={2}>
      <Paper
        variant="outlined"
        sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
      >
        <Stack spacing={1.25}>
          <TextField
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Digite o nome do tutor (ou pet)..."
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
              ),
              endAdornment: isLoading ? (
                <CircularProgress size={18} color="inherit" />
              ) : null,
            }}
          />

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Typography variant="body2" color="text.secondary">
              {filteredTutors.length} tutor{filteredTutors.length === 1 ? "" : "es"}{" "}
              na lista
            </Typography>

            <Button
              variant="outlined"
              size="small"
              sx={{ textTransform: "none" }}
              onClick={handleSelectAllClick}
              disabled={tutorsForSelection.length === 0}
            >
              {areAllSelected ? "Limpar seleção" : "Selecionar todos"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {hasError && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <Typography variant="body2" color="error">
            Erro ao buscar tutores. Tente novamente.
          </Typography>
        </Paper>
      )}

      {!hasError && tutorsForSelection.length === 0 && !isLoading && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum tutor encontrado.
          </Typography>
        </Paper>
      )}

      {!hasError && tutorsForSelection.length > 0 && filteredTutors.length === 0 && !isLoading && (
        <Paper
          variant="outlined"
          sx={{ p: 1.5, borderRadius: 2, bgcolor: "background.paper" }}
        >
          <Typography variant="body2" color="text.secondary">
            Nenhum tutor encontrado para este filtro.
          </Typography>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{
          borderRadius: 2,
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Stack
          spacing={1}
          sx={{
            p: 1.25,
            maxHeight: 360,
            overflowY: "auto",
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              borderRadius: "4px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0, 0, 0, 0.2)",
              borderRadius: "4px",
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.3)" },
            },
          }}
        >
          {paginatedTutors.map((tutor) => {
            const selected = isTutorSelected(selectedTutors, tutor);
            const group = groupedIndex[tutor.id];
            const petsLabel =
              group?.pets?.length && group.pets.length > 0
                ? `Pets: ${group.pets
                    .slice(0, 3)
                    .map((p) => p.fullName)
                    .filter(Boolean)
                    .join(", ")}${group.pets.length > 3 ? "…" : ""}`
                : null;

            return (
              <Paper
                key={tutor.id}
                elevation={0}
                sx={{
                  px: 2,
                  py: 1,
                  border: "1px solid",
                  borderColor: selected ? "primary.main" : "divider",
                  borderRadius: 1.5,
                  backgroundColor: selected
                    ? "rgba(25, 118, 210, 0.06)"
                    : "background.paper",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  position: "relative",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "rgba(25, 118, 210, 0.06)",
                    transform: "translateX(4px)",
                    boxShadow: 1,
                  },
                  ...(selected && {
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: "4px",
                      backgroundColor: "primary.main",
                      borderRadius: "4px 0 0 4px",
                    },
                  }),
                }}
                onClick={() => handleToggleTutor(tutor)}
                role="button"
                aria-label={`Selecionar tutor ${tutor.fullName}`}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selected ? (
                      <CheckBoxIcon sx={{ fontSize: 22, color: "primary.main" }} />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        sx={{ fontSize: 22, color: "text.secondary" }}
                      />
                    )}
                  </Box>

                  <Stack sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={700}
                      noWrap
                      sx={{ color: selected ? "primary.main" : "text.primary" }}
                    >
                      {tutor.fullName}
                    </Typography>

                    {(tutor.phone || tutor.cpf || petsLabel) && (
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {[tutor.phone, tutor.cpf, petsLabel]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    )}
                  </Stack>
                </Stack>
              </Paper>
            );
          })}
        </Stack>

        <Divider />

        <TablePagination
          component="div"
          count={filteredTutors.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          sx={{ px: 1 }}
        />
      </Paper>
    </Stack>
  );
}

